import {
  Contract,
  formatUnits,
  parseUnits,
  ethers,
  formatEther,
  BrowserProvider,

  JsonRpcProvider
} from "ethers";
import { TOKEN_ABI, PRESALE_ABI } from "../contracts";
import {
  useAppKitAccount,
  useAppKitNetwork,
  useAppKitProvider,
} from "@reown/appkit/react";
import { CHRLE_TOKEN_ADDRESS } from "../constant";
// import { useAccount, useBalance, useChainId } from "wagmi";
// import { useEthersSigner } from "./useEtherSigner";
import {  CLAIM_ABI ,PRESALE_CONTRACT_ADDRESS} from "../contracts";

// Define the two networks (BSC and Matic)
const NETWORKS = {
  56: {
    // Binance Smart Chain (BSC) Mainnet
    PRESALE_CONTRACT_ADDRESS: "0x755F9cB33b0AbB1d7b7EFD79D125C10C14a5aBFB",
    USDT_CONTRACT_ADDRESS: "0x55d398326f99059fF775485246999027B3197955",
    USDC_CONTRACT_ADDRESS: "0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d",
    TOKEN_CONTRACT_ADDRESS: "0x755F9cB33b0AbB1d7b7EFD79D125C10C14a5aBFB",
    decimals: 18,
  },

  1: {
    // Ethereum Mainnet
    PRESALE_CONTRACT_ADDRESS: "0x0F3E9821D773Ae72C1768531d442669Af9929841",
    USDT_CONTRACT_ADDRESS: "0xdac17f958d2ee523a2206206994597c13d831ec7",
    USDC_CONTRACT_ADDRESS: "0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48",
    TOKEN_CONTRACT_ADDRESS: "0x755F9cB33b0AbB1d7b7EFD79D125C10C14a5aBFB",
    decimals: 6,
  },
};

function useContract() {
  const { isConnected, address } = useAppKitAccount();
  const { chainId } = useAppKitNetwork();
  const { walletProvider } = useAppKitProvider("eip155");

  const getAppkitBalance = async (address) => {
    if (!walletProvider) return 0;
    const provider = new BrowserProvider(walletProvider, chainId);
    const balance = await provider.getBalance(address);
    return formatEther(balance);
  };

  const getProvider = async () => {
    if (!walletProvider || !chainId) return null;
    return new BrowserProvider(walletProvider, chainId);
  };

  const getSigner = async () => {
    if (!walletProvider || !chainId || !isConnected) return null;
    try {
      const provider = new BrowserProvider(walletProvider, chainId);
      const signer = await provider.getSigner();
      return signer;
    } catch (error) {
      console.error("Error getting signer:", error);
      return null;
    }
  };

  const getNetworkConfig = async () => {
    if (NETWORKS[chainId]) {
      const {
        PRESALE_CONTRACT_ADDRESS,
        USDT_CONTRACT_ADDRESS,
        USDC_CONTRACT_ADDRESS,
        TOKEN_CONTRACT_ADDRESS, // Include TOKEN_CONTRACT_ADDRESS here
        decimals,
      } = NETWORKS[chainId];
      return {
        chainId,
        PRESALE_CONTRACT_ADDRESS,
        USDT_CONTRACT_ADDRESS,
        USDC_CONTRACT_ADDRESS,
        TOKEN_CONTRACT_ADDRESS,
        decimals,
      };
    } else {
      throw new Error("Unsupported network.");
    }
  };

  const getContract = async (address, abi) => {
    const signer = await getSigner();
    const contract = new Contract(address, abi, signer);
    return contract;
  };

  const buy = async (paymentType, amount) => {
    try {
      // Fetch the correct contract addresses based on the network
      const {
        PRESALE_CONTRACT_ADDRESS,
        USDT_CONTRACT_ADDRESS,
        USDC_CONTRACT_ADDRESS,
        decimals,
      } = await getNetworkConfig();
      // Initialize the presale contract

      const contract = await getContract(PRESALE_CONTRACT_ADDRESS, PRESALE_ABI);
      if (
        paymentType === "ETH" ||
       
        paymentType === "BNB" 
      ) {
        // Handle ETH or BNB payment
        try {
          const transaction = await contract.buyFromNative({
            value: parseUnits(amount.toString(), 18),
          });
          const receipt = await transaction.wait();
          return receipt;
        } catch (error) {
          console.error("Native token purchase error:", {
            error,
            message: error.message,
            code: error.code,
            data: error.data,
            transaction: error.transaction,
          });
          throw error;
        }
      } else if (paymentType === "USDT") {
        // Handle USDT payment
        const usdt = await getContract(USDT_CONTRACT_ADDRESS, TOKEN_ABI);
        const approveTx = await usdt.approve(
          PRESALE_CONTRACT_ADDRESS,
          parseUnits(amount.toString(), decimals)
        );
        await approveTx.wait();
        const buyTx = await contract.buyFromToken(
          1,
          parseUnits(amount.toString(), decimals)
        ); // '1' represents USDT
        const receipt = await buyTx.wait();
        return receipt;
      } else if (paymentType === "USDC") {
        // Handle USDC payment
        const usdc = await getContract(USDC_CONTRACT_ADDRESS, TOKEN_ABI);
        const approveTx = await usdc.approve(
          PRESALE_CONTRACT_ADDRESS,
          parseUnits(amount.toString(), decimals)
        );
        await approveTx.wait();
        const buyTx = await contract.buyFromToken(
          2,
          parseUnits(amount.toString(), decimals)
        ); // '2' represents USDC
        const receipt = await buyTx.wait();
        return receipt;
      } else {
        throw new Error("Unsupported payment type");
      }
    } catch (error) {
      console.error("Error during purchase:", error);
      throw error;
    }
  };









  const getPresaleAllocation = async () => {
    const signer = await getSigner();
    if (!isConnected || !signer) {
      return 0;
    }
    try {
      // Get the provider and network information
      if (signer) {
        // Ensure we're connected to the correct network
        const networkConfig = NETWORKS[chainId];
        if (!networkConfig) {
          throw new Error("Unsupported network.");
        }
        const { PRESALE_CONTRACT_ADDRESS } = networkConfig; // Get the contract address for the current network
        // Get the contract instance
        const contract = await getContract(
          PRESALE_CONTRACT_ADDRESS,
          PRESALE_ABI
        );
        // Fetch the unclaimed tokens for the user
        const unclaimedTokens = await contract.getPresaleUnclaimed(address);
        const formattedUnclaimedTokens = formatUnits(unclaimedTokens, 18); // Format the tokens
        return formattedUnclaimedTokens;
      }
      return null;
    } catch (error) {
      // Handle any errors that may occur during the process
      console.error(
        "Error fetching presale allocation:",
        error.message || error
      );
    }
  };

  const getData = async () => {
    // Check if the wallet is connected
    const signer = await getSigner();
    if (!isConnected || !signer) {
      return { balanceInEth: 0, contractBalance: 0 }; // Return default values if not connected
    }
    try {
      // Get the provider and network information
      if (signer) {
        // Ensure we're connected to the correct network
        const { chain_id } = await getNetworkConfig(); // Get the correct chainId from the network config
        if (chainId !== chain_id) {
          throw new Error("Connected to the wrong network.");
        }
        // Fetch the appropriate contract address based on the network
        const { PRESALE_CONTRACT_ADDRESS, TOKEN_CONTRACT_ADDRESS } =
          await getNetworkConfig();
        // Get the token contract instance
        const token = await getContract(TOKEN_CONTRACT_ADDRESS, TOKEN_ABI);
        // Fetch the user's token balance
        const balance = await token.balanceOf(address);
        const balanceInEth = formatUnits(balance, 18); // Format the user's balance
        // Fetch the contract's token balance
        const contractBalanceInEth = await token.balanceOf(
          PRESALE_CONTRACT_ADDRESS
        );
        const contractBalance = formatUnits(contractBalanceInEth, 18); // Format the contract's balance
        return {
          balanceInEth,
          contractBalance,
        };
      }
      return { balanceInEth: 0, contractBalance: 0 };
    } catch (error) {
      // Handle any errors that may occur during the process
      console.error("Error fetching data:", error.message || error);
    }
  };

  // Get user's token balance
  const myTokenBalance = async () => {
    const signer = await getSigner();
    if (!isConnected || !signer) return 0;
    const { TOKEN_CONTRACT_ADDRESS } = await getNetworkConfig();
    const token = await getContract(TOKEN_CONTRACT_ADDRESS, TOKEN_ABI, signer);
    const balance = await token.balanceOf(address);
    return formatUnits(balance, 18);
  };

  const maxBalances = async () => {
    try {
      const signer = await getSigner();
      if (!isConnected || !signer) {
        return { usdt: "0.0000", usdc: "0.0000", eth: "0.0000", bnb: "0.0000" };
      }
      // Fetch the current network configuration
      const networkConfig = await getNetworkConfig();
      const { USDT_CONTRACT_ADDRESS, USDC_CONTRACT_ADDRESS } = networkConfig;
      if (!USDT_CONTRACT_ADDRESS || !USDC_CONTRACT_ADDRESS) {
        throw new Error(
          "USDT or USDC contract address not found for this network."
        );
      }
      // Get contract instances
      const usdt = await getContract(USDT_CONTRACT_ADDRESS, TOKEN_ABI, signer);
      const usdc = await getContract(USDC_CONTRACT_ADDRESS, TOKEN_ABI, signer);
      // Fetch balances
      const balance = await getAppkitBalance(address);
      const [usdtBalance, usdcBalance, ethBalance, bnbBalance] =
        await Promise.all([
          usdt.balanceOf(address),
          usdc.balanceOf(address),
          balance,
          balance,
        ]);
      // Return formatted balances
      return {
        usdt: formatUnits(usdtBalance, 6), // USDT typically has 6 decimals
        usdc: formatUnits(usdcBalance, 6), // USDC typically has 6 decimals
        eth: ethBalance, // ETH typically has 18 decimals
        bnb: bnbBalance, // BNB also has 18 decimals
      };
    } catch (error) {
      console.error("Error fetching balances:", error);
      return { usdt: "0.0000", usdc: "0.0000", eth: "0.0000", bnb: "0.0000" };
    }
  };


  const BSC_RPC = "https://bsc-dataseed.binance.org";




// Create a single BSC RPC provider
const bscProvider = new JsonRpcProvider(BSC_RPC);

const getPrice = async () => {
  try {
    const contract = new Contract(PRESALE_CONTRACT_ADDRESS, PRESALE_ABI, bscProvider);
    const price = await contract.perDollarPrice();
    return Number(formatUnits(price, 18)).toFixed(4);
  } catch (error) {
    console.error("Error fetching price from BSC:", error.message || error);
    return "0.0000";
  }
};


  // const getTotalTokensSoldAcrossNetworks = async () => {
  //   try {
  //     let totalTokens = 0;
  //     for (const networkId of Object.keys(NETWORKS)) {
  //       const provider = new ethers.JsonRpcProvider(
  //         "https://rpc.ankr.com/base"
  //       ); // Replace with relevant RPC URL
  //       const { PRESALE_CONTRACT_ADDRESS } = NETWORKS[networkId];
  //       const contract = new Contract(
  //         PRESALE_CONTRACT_ADDRESS,
  //         PRESALE_ABI,
  //         provider
  //       );
  //       const tokensSold = await contract.totalTokensSold();
  //       totalTokens += parseFloat(ethers.formatUnits(tokensSold, 18)); // Convert to decimal
  //     }
  //     return totalTokens.toFixed(2); // Return formatted value
  //   } catch (error) {
  //     console.error("Error fetching total tokens sold:", error);
  //     return "Error";
  //   }
  // };

  const getCHRLEBalance = async (address) => {
    try {
      const provider = new ethers.JsonRpcProvider(
        process.env.REACT_APP_STRIPE_BSC_RPC_URL
      );
      const contract = new ethers.Contract(
        CHRLE_TOKEN_ADDRESS,
        TOKEN_ABI,
        provider
      );
      const balance = await contract.balanceOf(address);
      const balanceFormatted = ethers.formatUnits(balance, 18);
      return Number(balanceFormatted);
    } catch (error) {
      console.error("get CHRLE balance error: ", error);
      return 0;
    }
  };

  const getBnbBalance = async (address) => {
    try {
      const RPC_URL = process.env.REACT_APP_STRIPE_BSC_RPC_URL;
      // const RPC_URL =
      //   "https://rpc.ankr.com/bsc_testnet_chapel/1b49ccfb464d3a36778d62cd26f29d2da87e099d5ed6229c15bc1627cee80453";
      const provider = new ethers.JsonRpcProvider(RPC_URL);
      const balanceWei = await provider.getBalance(address);
      const balanceBNB = ethers.formatEther(balanceWei);
      return parseFloat(balanceBNB);
    } catch (error) {
      console.error("get bnb balance error:", error);
      return null;
    }
  };










  // === NETWORK CONFIGURATION (without BSC) ===
const NETWORK_CONFIG = [
  {
    name: "Ethereum",
    chainId: 1,
    rpc: "https://rpc.ankr.com/base/44116265bd372b073764a46e8db5bebd09d449606f383178c6f18fdc20294656",
    presaleContract: "0x0F3E9821D773Ae72C1768531d442669Af9929841",
  },
  
];

// === BSC CONFIGURATION ===
const BSC_CONFIG = {
  name: "BSC",
  chainId: 56,
  rpc: "https://bsc-dataseed.binance.org/",
  presaleContract: "0x755F9cB33b0AbB1d7b7EFD79D125C10C14a5aBFB",
};





// Sum token_balance for all networks except BSC
const getUserTokenBalanceWithoutBsc = async (address) => {
  if (!address) return "0";
  let total = 0;
  for (const net of NETWORK_CONFIG) {
    try {
      const provider = new JsonRpcProvider(net.rpc, net.chainId);
      const contract = new Contract(net.presaleContract, PRESALE_ABI, provider);
      const userInfo = await contract.users(address);
      // Convert from wei (18 decimals) to integer tokens (no decimals)
      const tokenBalance = userInfo.token_balance?.toString() || "0";
      // Divide by 1e18 and remove decimals
      total += Math.floor(Number(tokenBalance) / 1e18);
    } catch (e) {
      console.error(`[${net.name}] Error fetching user token balance:`, e);
    }
  }
  return total.toString();
};



// Get token_balance on BSC only
// Get token_balance on BSC only, removing 18 decimals
const getUserBscTokenBalance = async (address) => {
  if (!address) return "0";
  try {
    const provider = new JsonRpcProvider(BSC_CONFIG.rpc, BSC_CONFIG.chainId);
    const contract = new Contract(BSC_CONFIG.presaleContract, PRESALE_ABI, provider);
    const userInfo = await contract.users(address);
    const tokenBalance = userInfo.token_balance?.toString() || "0";
    // Convert from wei to integer tokens (no decimals)
    const integerBalance = Math.floor(Number(tokenBalance) / 1e18);
    return integerBalance.toString();
  } catch (e) {
    console.error("[BSC] Error fetching user token balance:", e);
    return "0";
  }
};







const ETH_PRESALE = "0x0F3E9821D773Ae72C1768531d442669Af9929841";
const BSC_PRESALE = "0x755F9cB33b0AbB1d7b7EFD79D125C10C14a5aBFB";

const ETH_RPC = "https://mainnet.infura.io/v3/530c5ab4710840fe9a0d8482b7050658";



const formatNum = (num) => 
  Number(num || 0).toLocaleString("en-US", {
    maximumFractionDigits: 2,
  });

const getPresaleStats = async () => {
  try {
    const ethProvider = new ethers.JsonRpcProvider(
      "https://rpc.ankr.com/eth/1d40a90b355302e53e2d1639ca4da718e3c6be2cbc3d0b9fc7ff7158ff175fd8"
    );

    const bscProvider = new ethers.JsonRpcProvider(
      "https://bsc-dataseed.binance.org"
    );

    const ABI = [
      "function totalTokensSold() view returns (uint256)",
      "function perDollarPrice() view returns (uint256)",
    ];

    const ethPresale = new ethers.Contract(
      "0x0F3E9821D773Ae72C1768531d442669Af9929841",
      ABI,
      ethProvider
    );

    const bscPresale = new ethers.Contract(
      "0x755F9cB33b0AbB1d7b7EFD79D125C10C14a5aBFB",
      ABI,
      bscProvider
    );

    const [
      ethSoldRaw,
      bscSoldRaw,
      ethPerDollarRaw,
      bscPerDollarRaw,
    ] = await Promise.all([
      ethPresale.totalTokensSold(),
      bscPresale.totalTokensSold(),
      ethPresale.perDollarPrice(),
      bscPresale.perDollarPrice(),
    ]);

    const ethSold = Number(ethers.formatUnits(ethSoldRaw, 18));
    const bscSold = Number(ethers.formatUnits(bscSoldRaw, 18));

    const ethPerDollar = Number(ethPerDollarRaw);
    const bscPerDollar = Number(bscPerDollarRaw);

    const ethRaised = ethPerDollar > 0 ? ethSold / ethPerDollar : 0;
    const bscRaised = bscPerDollar > 0 ? bscSold / bscPerDollar : 0;

    return {
      tokenSold: ethSold + bscSold,
      usdRaised: ethRaised + bscRaised,
      ethSold,
      bscSold,
      ethRaised,
      bscRaised,
    };
  } catch (error) {
    console.error("getPresaleStats error:", error);

    return {
      tokenSold: 0,
      usdRaised: 0,
      ethSold: 0,
      bscSold: 0,
      ethRaised: 0,
      bscRaised: 0,
    };
  }
};

  return {
    getAppkitBalance,
    getProvider,
    getSigner,
    buy,
    getData,
    myTokenBalance,
    maxBalances,
    getPrice,

    getPresaleAllocation,
    // getTotalTokensSoldAcrossNetworks,
  
    getCHRLEBalance,
    getBnbBalance,
    getUserBscTokenBalance,
    getUserTokenBalanceWithoutBsc,
    getPresaleStats
  };
}

export default useContract;
